import { useState, useEffect, useRef } from "react";
import {
  Wheat,
  Truck,
  Scale,
  Sparkles,
  Zap,
  Store,
  ChevronRight,
  ShieldCheck,
  ShoppingBag,
} from "lucide-react";
import { cn } from "../lib/cn";

export interface MandiFact {
  tag: string;
  emoji: string;
  title: string;
  description: string;
  knNote?: string;
}

export const MANDI_FACTS: MandiFact[] = [
  {
    tag: "TRUSTED & RELIABLE SOURCING",
    emoji: "🛡️",
    title: "Rigorous QC & Multi-Stage Quality Testing",
    description:
      "Every staple batch is sourced from trusted, reliable partner mills and passes strict QC & quality testing before dispatch.",
    knNote: "ವಿಶ್ವಾಸಾರ್ಹ ಮೂಲಗಳಿಂದ ಗುಣಮಟ್ಟ ಪರೀಕ್ಷಿಸಿದ ಆಹಾರ ಧಾನ್ಯಗಳು",
  },
  {
    tag: "DIRECT SAVINGS",
    emoji: "💰",
    title: "Direct From Verified Millers",
    description:
      "Eliminating intermediary tiers saves commercial kitchens and retail stores 8% to 14% on every 25kg bulk bag.",
    knNote: "ನೇರವಾಗಿ ಮಿಲ್‌ಗಳಿಂದ 8% ರಿಂದ 14% ವರೆಗೆ ಉಳಿತಾಯ",
  },
  {
    tag: "STAPLES QUALITY",
    emoji: "🍚",
    title: "12+ Months Aged Sona Masoori",
    description:
      "Paddy aged 12+ months cooks fluffy without stickiness and expands up to 2.5x in volume for commercial kitchens.",
    knNote: "ಹಳೆಯ ಸೋನಾ ಮಸೂರಿ ಅಕ್ಕಿ ಹೆಚ್ಚು ಅನ್ನ ನೀಡುತ್ತದೆ",
  },
  {
    tag: "LOGISTICS DISPATCH",
    emoji: "🚚",
    title: "Guaranteed Morning Milk-Runs",
    description:
      "Orders placed by 10 PM are sorted at the hub by 2 AM and delivered between 6:00 AM – 9:00 AM directly to your store.",
    knNote: "ಬೆಳಗಿನ ಜಾವ 6 ರಿಂದ 9 ಗಂಟೆಗೆ ಅಂಗಡಿ ಬಾಗಿಲಿಗೆ ವಿತರಣೆ",
  },
  {
    tag: "QUALITY TESTING",
    emoji: "🔍",
    title: "Moisture & Density Spot-Checks",
    description:
      "Every batch of Toor Dal, Rice, and Wheat undergoes electronic moisture and impurity testing before warehouse stocking.",
    knNote: "ಗುಣಮಟ್ಟ ಪರಿಶೀಲಿಸಿದ ಶುದ್ಧ ಆಹಾರ ಧಾನ್ಯಗಳು",
  },
  {
    tag: "FAIR PRICING",
    emoji: "⚖️",
    title: "Exact Tare-Weight Guarantee",
    description:
      "Every bulk bag is tare-weighed at the loading dock so you receive exactly what you paid for down to the last gram.",
    knNote: "ಖಚಿತ ತೂಕ ಹಾಗೂ ನಿಖರ ಪ್ರಮಾಣ ಖಾತರಿ",
  },
  {
    tag: "FOOD SAFETY",
    emoji: "🛡️",
    title: "100% FSSAI Certified Supply",
    description:
      "Food safety registration is verified across all partner millers and edible oil refiners for genuine wholesale purity.",
    knNote: "FSSAI ಪ್ರಮಾಣೀಕೃತ ಸುರಕ್ಷಿತ ಧಾನ್ಯಗಳು",
  },
];

const ICONS = [Wheat, Truck, Scale, Sparkles, Store, ShoppingBag];

export interface InteractiveLoaderProps {
  title?: string;
  subtitle?: string;
  className?: string;
  compact?: boolean;
}

export function InteractiveLoader({
  title = "Connecting to Quality-Tested Wholesale Catalog...",
  subtitle = "Fetching live wholesale rates & QC-verified stock",
  className,
  compact = false,
}: InteractiveLoaderProps) {
  const [factIndex, setFactIndex] = useState(0);
  const [boostCount, setBoostCount] = useState(0);
  const [isBoosted, setIsBoosted] = useState(false);
  const [iconIndex, setIconIndex] = useState(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Cycle facts every 3.2 seconds
  useEffect(() => {
    timerRef.current = setInterval(() => {
      setFactIndex((prev) => (prev + 1) % MANDI_FACTS.length);
      setIconIndex((prev) => (prev + 1) % ICONS.length);
    }, 3200);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const handleNextFact = (e: React.MouseEvent) => {
    e.stopPropagation();
    setFactIndex((prev) => (prev + 1) % MANDI_FACTS.length);
    setIconIndex((prev) => (prev + 1) % ICONS.length);
  };

  const handleBoost = (e: React.MouseEvent) => {
    e.stopPropagation();
    setBoostCount((prev) => prev + 1);
    setIsBoosted(true);
    setTimeout(() => setIsBoosted(false), 400);
  };

  const currentFact = MANDI_FACTS[factIndex];
  const CurrentIcon = ICONS[iconIndex];

  if (compact) {
    return (
      <div
        className={cn(
          "p-3.5 sm:p-4 rounded-2xl bg-gradient-to-r from-card/90 via-card to-card/90 border border-border shadow-xs flex items-center justify-between gap-3 text-xs animate-in fade-in select-none",
          className
        )}
      >
        <div className="flex items-center gap-3 min-w-0">
          {/* Compact Official Logo Avatar with rotating icon badge */}
          <div className="relative shrink-0 cursor-pointer" onClick={handleBoost} title="Tap to speed up loading!">
            <div className="h-11 w-11 rounded-xl bg-slate-950 overflow-hidden ring-1 ring-emerald-500/50 shadow-md relative">
              <img
                src="/my-daily-direct-logo.jpg"
                alt="MyDailyDirect Logo"
                className={cn(
                  "h-full w-full object-cover transition-transform",
                  isBoosted ? "scale-115" : "scale-100"
                )}
                onError={(e) => {
                  (e.target as HTMLImageElement).src = "/my-daily-direct-logo.jpg";
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-emerald-500/20 via-transparent to-sky-400/20 pointer-events-none" />
            </div>

            {/* Overlapping mini activity icon */}
            <div className="absolute -bottom-1 -right-1 h-5 w-5 rounded-md bg-card border border-primary/40 shadow-xs flex items-center justify-center text-primary">
              <CurrentIcon className="h-3 w-3" />
            </div>

            {/* Live ping */}
            <span className="absolute -top-1 -left-1 flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500 border border-background" />
            </span>
          </div>

          <div className="min-w-0">
            <div className="font-bold text-foreground flex items-center gap-1.5 truncate">
              <span className="font-extrabold bg-gradient-to-r from-emerald-500 via-teal-500 to-sky-500 bg-clip-text text-transparent">
                MyDailyDirect:
              </span>
              <span className="truncate">{title}</span>
              <span className="text-[10px] text-muted-foreground font-mono shrink-0">
                ({boostCount > 0 ? `${boostCount + 1}x Boosted` : "QC Verified"})
              </span>
            </div>
            <p className="text-2xs text-muted-foreground line-clamp-1 truncate">
              <span className="font-semibold text-foreground/90">{currentFact.title}:</span> {currentFact.description}
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={handleNextFact}
          className="shrink-0 px-2.5 py-1 text-2xs font-semibold rounded-lg bg-muted/60 hover:bg-muted text-foreground border border-border flex items-center gap-1 transition-all hover:scale-105 active:scale-95"
          title="Click to read another mandi insight"
        >
          <span>💡 Next tip</span>
          <ChevronRight className="h-3 w-3" />
        </button>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "relative flex flex-col items-center justify-center p-6 sm:p-10 text-center max-w-lg mx-auto space-y-6 select-none",
        className
      )}
    >
      {/* Background Ambient Glow */}
      <div className="absolute inset-0 pointer-events-none -z-10 flex items-center justify-center overflow-hidden">
        <div className="w-72 h-72 rounded-full bg-gradient-to-tr from-emerald-500/15 via-teal-500/10 to-amber-500/15 blur-3xl opacity-70 animate-pulse" />
      </div>

      {/* Main Animated Brand Emblem: Official MyDailyDirect Logo + Dynamic Activity Badge */}
      <div className="relative group cursor-pointer" onClick={handleBoost} title="Tap to speed up loading!">
        {/* Concentric Pulse Rings */}
        <div className="absolute -inset-3 rounded-3xl bg-gradient-to-r from-emerald-500/30 via-teal-500/20 to-sky-500/30 blur-lg opacity-80 animate-ping" />
        
        <div className="relative h-24 w-24 rounded-3xl bg-slate-950 border-2 border-emerald-500/60 shadow-2xl overflow-hidden flex items-center justify-center transition-all duration-300 group-hover:scale-110 active:scale-95 group-hover:border-emerald-400 group-hover:shadow-emerald-500/30">
          {/* Official MyDailyDirect Brand Logo */}
          <img
            src="/my-daily-direct-logo.jpg"
            alt="MyDailyDirect"
            className={cn(
              "h-full w-full object-cover transition-transform duration-500",
              isBoosted ? "scale-110" : "scale-100"
            )}
            onError={(e) => {
              (e.target as HTMLImageElement).src = "/my-daily-direct-logo.jpg";
            }}
          />
          {/* Moving gloss sheen overlay across the logo */}
          <div className="absolute inset-0 bg-gradient-to-tr from-emerald-500/25 via-white/10 to-sky-400/25 pointer-events-none" />
          <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-white/30 to-transparent pointer-events-none" />
        </div>

        {/* Dynamic Rotating Commodity / Service Badge chip overlapping bottom-right of logo */}
        <div className="absolute -bottom-2 -right-2 h-9 w-9 rounded-xl bg-card border-2 border-primary/50 shadow-md flex items-center justify-center text-primary transition-all duration-300 group-hover:scale-110">
          <CurrentIcon
            className={cn(
              "h-4 w-4 transition-all duration-300",
              isBoosted ? "scale-125 text-amber-500" : "animate-bounce"
            )}
          />
        </div>

        {/* Active live connection dot at top-left */}
        <span className="absolute -top-1.5 -left-1.5 flex h-4 w-4">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-4 w-4 bg-emerald-500 border-2 border-background" />
        </span>
      </div>

      {/* Brand Title with Wordmark */}
      <div className="space-y-1">
        <div className="flex items-center justify-center gap-2">
          <span className="font-extrabold text-base tracking-tight bg-gradient-to-r from-emerald-500 via-teal-500 to-sky-500 bg-clip-text text-transparent">
            MyDailyDirect
          </span>
          <span className="text-2xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">
            QC-Verified Wholesale
          </span>
        </div>
        <h3 className="text-sm sm:text-base font-black tracking-tight text-foreground flex items-center justify-center gap-2">
          <span>{title}</span>
          <Sparkles className="h-4 w-4 text-amber-500 animate-spin" />
        </h3>
        <p className="text-xs text-muted-foreground font-medium max-w-sm mx-auto">
          {subtitle}
        </p>
      </div>

      {/* Dynamic Shimmer Progress Bar */}
      <div className="w-full max-w-xs space-y-1.5">
        <div className="relative h-2 w-full overflow-hidden rounded-full bg-muted/60 border border-border/80 p-0.5">
          <div className="absolute inset-0 bg-gradient-to-r from-primary via-emerald-500 to-amber-500 rounded-full animate-[shimmer_1.8s_infinite] w-full" />
        </div>
        <div className="flex items-center justify-between text-[11px] text-muted-foreground font-mono">
          <span className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400 font-semibold">
            <ShieldCheck className="h-3 w-3" /> Live Mandi Sync
          </span>
          <span>{boostCount > 0 ? `⚡ ${boostCount}x Speed Boost` : "Securing Prices..."}</span>
        </div>
      </div>

      {/* Interactive Mandi Fact Card: Keeps user entertained! */}
      <div
        onClick={handleNextFact}
        className="w-full text-left p-4 rounded-2xl bg-card/90 border border-border/80 shadow-sm hover:border-primary/40 hover:shadow-md transition-all cursor-pointer group space-y-2 backdrop-blur-xs relative overflow-hidden"
      >
        {/* Shimmer sweep over the card */}
        <div className="absolute inset-0 -translate-x-full group-hover:animate-[shimmer_1.2s_infinite] bg-gradient-to-r from-transparent via-primary/5 to-transparent pointer-events-none" />

        <div className="flex items-center justify-between">
          <span className="inline-flex items-center gap-1.5 text-2xs font-extrabold tracking-wider uppercase bg-primary/10 text-primary border border-primary/20 px-2 py-0.5 rounded-full">
            <span>{currentFact.emoji}</span>
            <span>{currentFact.tag}</span>
          </span>

          <span className="text-[11px] text-muted-foreground font-semibold group-hover:text-primary flex items-center gap-0.5 transition-colors">
            Next fact <ChevronRight className="h-3 w-3 group-hover:translate-x-0.5 transition-transform" />
          </span>
        </div>

        <div className="space-y-1 pt-0.5">
          <h4 className="text-xs sm:text-sm font-bold text-foreground">
            {currentFact.title}
          </h4>
          <p className="text-xs text-muted-foreground leading-relaxed">
            {currentFact.description}
          </p>
          {currentFact.knNote && (
            <p className="text-2xs text-muted-foreground/80 font-medium italic pt-0.5">
              ಕನ್ನಡ: {currentFact.knNote}
            </p>
          )}
        </div>
      </div>

      {/* Interactive Micro-Boost / Tap Action */}
      <div className="flex items-center justify-center gap-2 pt-1">
        <button
          type="button"
          onClick={handleBoost}
          className={cn(
            "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-2xs font-bold transition-all border shadow-xs active:scale-95",
            boostCount > 0
              ? "bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30 hover:bg-amber-500/20"
              : "bg-muted/70 text-muted-foreground border-border hover:text-foreground hover:bg-muted"
          )}
        >
          <Zap className={cn("h-3.5 w-3.5", boostCount > 0 ? "text-amber-500 fill-amber-500" : "")} />
          <span>{boostCount === 0 ? "⚡ Tap to speed up loading" : `⚡ Boosted ${boostCount} times!`}</span>
        </button>

        <button
          type="button"
          onClick={handleNextFact}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-2xs font-bold bg-muted/70 text-muted-foreground border border-border hover:text-foreground hover:bg-muted transition-all active:scale-95"
        >
          <Sparkles className="h-3.5 w-3.5 text-primary" />
          <span>More quality facts ({factIndex + 1}/{MANDI_FACTS.length})</span>
        </button>
      </div>
    </div>
  );
}

/** Full page loader that wraps InteractiveLoader with comfortable screen placement */
export function PageLoadingScreen({
  title,
  subtitle,
  className,
}: {
  title?: string;
  subtitle?: string;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "min-h-[60vh] flex flex-col items-center justify-center p-4 animate-in fade-in duration-300",
        className
      )}
    >
      <InteractiveLoader title={title} subtitle={subtitle} />
    </div>
  );
}
